import { useState, useCallback, useEffect } from "react";
import AssetTile from "../AssetTile/AssetTile";
import { GroupedInvestmentCategory } from "../../types";
import { useUpdateInvestmentPreferences } from "hooks/investments";
import "./AssetTilesList.scss";

interface AssetTilesListProps {
  categories: Record<string, GroupedInvestmentCategory>;
  title?: string;
}

const AssetTilesList = ({ categories, title }: AssetTilesListProps) => {
  const getSortedCategories = useCallback(() => {
    return Object.values(categories).sort((a, b) => a.order - b.order);
  }, [categories]);

  const [sortedCategories, setSortedCategories] =
    useState<GroupedInvestmentCategory[]>(getSortedCategories);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const { mutate: updatePreferences } = useUpdateInvestmentPreferences();

  useEffect(() => {
    setSortedCategories(getSortedCategories());
  }, [getSortedCategories]);

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex === null || dragOverIndex === null) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newCategories = [...sortedCategories];
    const [draggedItem] = newCategories.splice(dragIndex, 1);
    newCategories.splice(dragOverIndex, 0, draggedItem);

    const reorderedCategories = newCategories.map((cat, index) => ({
      ...cat,
      order: index,
    }));

    setSortedCategories(reorderedCategories);

    const preferences = reorderedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    updatePreferences({ preferences });

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleToggleExpand = (slug: string, isExpanded: boolean) => {
    const updatedCategories = sortedCategories.map((cat) =>
      cat.investmentType.slug === slug ? { ...cat, isExpanded } : cat,
    );

    setSortedCategories(updatedCategories);

    const preferences = updatedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    updatePreferences({ preferences });
  };

  if (sortedCategories.length === 0) {
    return null;
  }

  return (
    <div className="asset-tiles-list">
      {title && <h3 className="asset-tiles-list__title">{title}</h3>}
      <div className="asset-tiles-list__items">
        {sortedCategories.map((category, index) => (
          <AssetTile
            key={category.investmentType.slug}
            category={category}
            index={index}
            onToggleExpand={handleToggleExpand}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            isDragging={dragIndex === index}
            isDragOver={dragOverIndex === index}
          />
        ))}
      </div>
    </div>
  );
};

export default AssetTilesList;
